export function styles(accent: string, position: 'right' | 'left', zIndex: number): string {
  const side = position === 'left' ? 'left' : 'right';
  return `
:host { all: initial; }
* { box-sizing: border-box; }
[hidden] { display: none !important; }
.root {
  --accent: ${accent};
  --accent-ink: #ffffff;
  --bg: #ffffff;
  --bg-soft: #f6f7f9;
  --bg-sunk: #eef0f3;
  --ink: #111827;
  --ink-2: #4b5563;
  --ink-3: #8a93a1;
  --line: #e5e7eb;
  --user: color-mix(in srgb, var(--accent) 12%, #ffffff);
  --warn: #b45309;
  --warn-bg: #fff7ed;
  --ok: #047857;
  --ok-bg: #ecfdf5;
  --err: #b91c1c;
  --shadow: 0 12px 40px rgba(15, 23, 42, .18), 0 2px 8px rgba(15, 23, 42, .08);
  font: 400 14px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--ink);
  position: fixed; ${side}: 20px; bottom: 20px; z-index: ${zIndex};
  -webkit-font-smoothing: antialiased;
}
@media (prefers-color-scheme: dark) {
  .root:not(.light) {
    --bg: #15171c; --bg-soft: #1c1f26; --bg-sunk: #242832; --ink: #eef0f4; --ink-2: #b4bac5; --ink-3: #7d8594;
    --line: #2c313b; --user: color-mix(in srgb, var(--accent) 28%, #15171c); --warn-bg: #3a2a12; --warn: #fbbf24;
    --ok-bg: #0f2e25; --ok: #34d399; --err: #f87171; --shadow: 0 12px 40px rgba(0,0,0,.5);
  }
}
.root.dark {
  --bg: #15171c; --bg-soft: #1c1f26; --bg-sunk: #242832; --ink: #eef0f4; --ink-2: #b4bac5; --ink-3: #7d8594;
  --line: #2c313b; --user: color-mix(in srgb, var(--accent) 28%, #15171c); --warn-bg: #3a2a12; --warn: #fbbf24;
  --ok-bg: #0f2e25; --ok: #34d399; --err: #f87171; --shadow: 0 12px 40px rgba(0,0,0,.5);
}
.root.hidden-for-capture { visibility: hidden; }
button { font: inherit; color: inherit; cursor: pointer; border: 0; background: none; padding: 0; }
button:focus-visible, textarea:focus-visible, input:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.launcher {
  width: 52px; height: 52px; border-radius: 50%; background: var(--accent); color: var(--accent-ink);
  display: grid; place-items: center; box-shadow: var(--shadow); position: relative; transition: transform .15s ease;
}
.launcher:hover { transform: translateY(-1px); }
.launcher svg { width: 24px; height: 24px; }
.badge {
  position: absolute; top: -2px; ${side === 'right' ? 'right' : 'left'}: -2px; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 9px; background: #ef4444; color: #fff; font: 600 11px/18px system-ui, sans-serif; text-align: center;
}
.panel {
  position: absolute; ${side}: 0; bottom: 64px; width: 380px; height: min(640px, calc(100vh - 110px));
  background: var(--bg); border: 1px solid var(--line); border-radius: 16px; box-shadow: var(--shadow);
  display: flex; flex-direction: column; overflow: hidden;
}
.panel[hidden] { display: none; }
@media (max-width: 480px) {
  .root { ${side}: 12px; bottom: 12px; }
  .panel { position: fixed; inset: 0; width: auto; height: auto; border-radius: 0; bottom: 0; }
}
.head { padding: 14px 16px 0; border-bottom: 1px solid var(--line); background: var(--bg); }
.head-row { display: flex; align-items: center; gap: 8px; }
.head h2 { margin: 0; font-size: 16px; font-weight: 650; letter-spacing: -.01em; flex: 1; }
.head p { margin: 2px 0 10px; color: var(--ink-3); font-size: 12.5px; }
.icon-btn { width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center; color: var(--ink-2); }
.icon-btn:hover { background: var(--bg-sunk); color: var(--ink); }
.tabs { display: flex; gap: 4px; }
.tab { padding: 8px 10px; border-bottom: 2px solid transparent; color: var(--ink-3); font-weight: 550; display: inline-flex; gap: 6px; align-items: center; }
.tab[aria-selected="true"] { color: var(--ink); border-bottom-color: var(--accent); }
.tab .count { font-size: 11px; background: var(--bg-sunk); color: var(--ink-2); border-radius: 8px; padding: 0 6px; }
.view { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.view[hidden] { display: none; }
.list { flex: 1; overflow-y: auto; padding: 12px 14px 4px; background: var(--bg-soft); }
.empty { color: var(--ink-3); text-align: center; padding: 40px 18px; font-size: 13.5px; }
.divider { display: flex; align-items: center; gap: 8px; margin: 14px 0 8px; color: var(--ink-3); font-size: 12px; white-space: nowrap; }
.divider span { flex: none; }
.divider::before, .divider::after { content: ""; flex: 1; height: 1px; background: var(--line); }
.divider b { color: var(--ink-2); font-weight: 600; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; border-radius: 999px; padding: 1px 8px; background: var(--bg-sunk); color: var(--ink-2); white-space: nowrap; }
.pill.ok { background: var(--ok-bg); color: var(--ok); }
.pill.warn { background: var(--warn-bg); color: var(--warn); }
.pill.accent { background: color-mix(in srgb, var(--accent) 14%, transparent); color: var(--accent); }
.msg { display: flex; flex-direction: column; margin: 6px 0; max-width: 88%; }
.msg.user { margin-left: auto; align-items: flex-end; }
.bubble { background: var(--bg); border: 1px solid var(--line); border-radius: 14px; padding: 8px 11px; white-space: pre-wrap; word-wrap: break-word; }
.msg.user .bubble { background: var(--user); border-color: transparent; border-bottom-right-radius: 4px; }
.msg.agent .bubble { border-bottom-left-radius: 4px; }
.msg.question .bubble { border-color: color-mix(in srgb, var(--accent) 45%, var(--line)); }
.who { font-size: 11.5px; color: var(--ink-3); margin: 0 4px 2px; display: flex; gap: 6px; align-items: center; }
.meta { font-size: 11px; color: var(--ink-3); margin: 3px 4px 0; display: flex; gap: 8px; align-items: center; }
.meta svg { width: 12px; height: 12px; }
.meta .linked { color: var(--ok); display: inline-flex; }
.sys { text-align: center; margin: 8px 0; }
.sys span { display: inline-block; font-size: 12px; color: var(--ink-2); background: var(--bg-sunk); border-radius: 10px; padding: 3px 10px; }
.shots { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; justify-content: flex-end; }
.shot { position: relative; border-radius: 8px; overflow: hidden; border: 1px solid var(--line); background: var(--bg); }
.shot img { display: block; height: 96px; width: auto; min-width: 60px; max-width: 180px; object-fit: cover; }
.shot .cap { position: absolute; left: 0; right: 0; bottom: 0; font-size: 10.5px; color: #fff; background: linear-gradient(transparent, rgba(0,0,0,.65)); padding: 8px 6px 3px; }
.voice { margin-top: 4px; }
.voice audio { width: 220px; height: 34px; display: block; }
.transcript { margin-top: 4px; font-size: 13px; color: var(--ink-2); font-style: italic; }
.options { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.chip { border: 1px solid color-mix(in srgb, var(--accent) 45%, var(--line)); color: var(--accent); border-radius: 999px; padding: 4px 11px; font-size: 13px; background: var(--bg); }
.chip:hover { background: color-mix(in srgb, var(--accent) 10%, var(--bg)); }
.fork-card { background: var(--bg); border: 1px solid color-mix(in srgb, var(--accent) 40%, var(--line)); border-radius: 14px; padding: 12px; margin: 10px 0; }
.fork-card h4 { margin: 0 0 4px; font-size: 14px; display: flex; gap: 6px; align-items: center; }
.fork-card p { margin: 0 0 10px; color: var(--ink-2); font-size: 13px; }
.actions { display: flex; flex-wrap: wrap; gap: 6px; }
.btn { display: inline-flex; align-items: center; gap: 6px; border-radius: 9px; padding: 7px 12px; font-weight: 600; font-size: 13px; border: 1px solid var(--line); background: var(--bg); }
.btn:hover { background: var(--bg-sunk); }
.btn.primary { background: var(--accent); border-color: var(--accent); color: var(--accent-ink); }
.btn.primary:hover { filter: brightness(1.05); }
.btn[disabled] { opacity: .55; cursor: default; }
.btn svg { width: 15px; height: 15px; }
.composer { border-top: 1px solid var(--line); padding: 8px 10px 10px; background: var(--bg); }
.target { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-3); padding: 0 4px 6px; min-height: 22px; white-space: nowrap; }
.target > span { flex: none; }
.target b { color: var(--ink-2); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1 1 auto; min-width: 0; }
.target .link { flex: none; }
.link { color: var(--accent); font-size: 12px; font-weight: 550; }
.link:hover { text-decoration: underline; }
.target .spacer { flex: 1 1 0; }
.tray { display: flex; gap: 6px; flex-wrap: wrap; padding: 0 2px 6px; }
.tray:empty { display: none; }
.att { position: relative; border: 1px solid var(--line); border-radius: 8px; background: var(--bg-soft); display: flex; align-items: center; gap: 6px; padding: 3px; }
.att img { width: 64px; height: 44px; object-fit: cover; border-radius: 5px; display: block; }
.att .lbl { font-size: 11px; color: var(--ink-2); padding-right: 22px; }
.att audio { height: 30px; width: 170px; }
.att .rm { position: absolute; top: 2px; right: 2px; width: 20px; height: 20px; border-radius: 50%; background: var(--bg); color: var(--ink-2); display: grid; place-items: center; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.att .rm svg { width: 12px; height: 12px; }
.row { display: flex; align-items: flex-end; gap: 4px; background: var(--bg-soft); border: 1px solid var(--line); border-radius: 12px; padding: 4px; }
.row:focus-within { border-color: color-mix(in srgb, var(--accent) 60%, var(--line)); }
textarea { flex: 1; resize: none; border: 0; background: transparent; color: var(--ink); font: inherit; padding: 7px 8px; max-height: 140px; min-height: 36px; outline: none; }
textarea::placeholder { color: var(--ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
textarea:focus-visible { outline: none; }
.tool { width: 36px; height: 36px; border-radius: 9px; display: grid; place-items: center; color: var(--ink-2); flex: none; }
.tool:hover { background: var(--bg-sunk); color: var(--ink); }
.tool.rec { color: #fff; background: #dc2626; }
.tool.send { background: var(--accent); color: var(--accent-ink); }
.tool.send[disabled] { opacity: .45; cursor: default; }
.recbar { display: flex; align-items: center; gap: 8px; flex: 1; padding: 0 8px; color: var(--ink-2); font-size: 13px; min-height: 36px; }
.dot { width: 8px; height: 8px; border-radius: 50%; background: #dc2626; animation: blink 1s infinite; }
.level { flex: 1; height: 4px; background: var(--bg-sunk); border-radius: 2px; overflow: hidden; }
.level i { display: block; height: 100%; background: #dc2626; width: 0; transition: width .08s linear; }
@keyframes blink { 50% { opacity: .3; } }
.foot { display: flex; align-items: center; gap: 6px; padding: 6px 4px 0; font-size: 11.5px; color: var(--ink-3); white-space: nowrap; }
.foot label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
.foot input { accent-color: var(--accent); margin: 0; }
.foot .spacer { flex: 1; }
.err { color: var(--err); font-size: 12px; padding: 4px 4px 0; }
.forks { flex: 1; overflow-y: auto; padding: 12px 14px; background: var(--bg-soft); }
.forks h3 { font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: var(--ink-3); margin: 14px 2px 8px; font-weight: 650; }
.forks h3:first-child { margin-top: 2px; }
.fork { background: var(--bg); border: 1px solid var(--line); border-radius: 12px; padding: 10px 12px; margin-bottom: 8px; display: grid; grid-template-columns: 1fr auto; gap: 4px 10px; align-items: center; }
.fork.active { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent) inset; }
.fork .name { font-weight: 650; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.fork .desc { grid-column: 1 / -1; color: var(--ink-2); font-size: 12.5px; }
.fork .sub { grid-column: 1 / -1; color: var(--ink-3); font-size: 11.5px; }
.lightbox { position: fixed; inset: 0; background: rgba(0,0,0,.8); display: grid; place-items: center; z-index: 1; padding: 20px; }
.lightbox img { max-width: 100%; max-height: 90vh; border-radius: 8px; background: #fff; }
.offline { background: var(--warn-bg); color: var(--warn); font-size: 12px; text-align: center; padding: 3px; }
.more { display: block; margin: 0 auto 8px; }
.tool.rec.rec { color: #fff; background: #dc2626; }
.launcher .recdot { position: absolute; top: -2px; left: -2px; width: 14px; height: 14px; border-radius: 50%; background: #dc2626; border: 2px solid var(--bg); animation: blink 1s infinite; }
.foot .live { color: #dc2626; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
.foot .dot { width: 7px; height: 7px; }
.att.file { padding: 4px 8px; }
.att.file svg { width: 16px; height: 16px; color: var(--ink-2); flex: none; }
.att.file .lbl b { font-weight: 600; color: var(--ink); max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: bottom; }
.files { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.filechip { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--line); border-radius: 8px; padding: 4px 8px; background: var(--bg-soft); color: var(--ink); text-decoration: none; font-size: 12.5px; max-width: 100%; }
.filechip svg { width: 14px; height: 14px; flex: none; color: var(--ink-2); }
.filechip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filechip em { font-style: normal; color: var(--ink-3); font-size: 11px; }
.fork .actions { margin-top: 2px; }
.log { margin: 4px 0 0; padding-left: 18px; color: var(--ink-2); font-size: 13px; }
.log li { margin: 3px 0; }
.log .link { font-size: 13px; text-align: left; }
`;
}
